import { Box, Drawer, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { ReactNode, SyntheticEvent } from "react";
import { useRef } from "react";

const SmallDrawerHandle = styled("div", {
	name: "SmallDrawer",
	slot: "Handle",
})({});

const SmallDrawerDivider = styled("div", {
	name: "SmallDrawer",
	slot: "Divider",
})({});

type Props = {
	open: boolean;
	onClose: () => void;
	title?: string;
	children: ReactNode;
};

export const SmallDrawer = ({ open, onClose, title, children }: Props) => {
	const startYRef = useRef<number | null>(null);
	const closedRef = useRef(false);
	const threshold = 60;

	const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		startYRef.current =
			e.touches?.[0]?.clientY ?? e.targetTouches?.[0]?.clientY ?? null;
		e.stopPropagation();
	};

	const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
		if (startYRef.current != null) {
			const y =
				e.touches?.[0]?.clientY ?? e.targetTouches?.[0]?.clientY ?? null;
			if (
				y != null &&
				y - startYRef.current > threshold &&
				!closedRef.current
			) {
				closedRef.current = true;
				onClose();
			}
		}
		e.stopPropagation();
	};

	const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
		startYRef.current = null;
		closedRef.current = false;
		e.stopPropagation();
	};

	return (
		<Drawer
			anchor="bottom"
			open={open}
			onClose={() => onClose()}
			ModalProps={{
				sx: { zIndex: (t) => t.zIndex.drawer + 1 },
			}}
			slotProps={{
				paper: {
					sx: {
						paddingBottom: "min(env(safe-area-inset-bottom, 0px), 10px)",
					},
					onClick: (e: SyntheticEvent) => {
						e.stopPropagation();
					},
				},
			}}
		>
			<Stack
				component="header"
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				sx={{ touchAction: "none" }}
			>
				<Stack alignItems="center" py={1}>
					<SmallDrawerHandle />
				</Stack>
				{title && (
					<Box px={1.6} pb={1.2}>
						<Typography variant="h3" textAlign="left">
							{title}
						</Typography>
					</Box>
				)}
			</Stack>
			<SmallDrawerDivider />
			<Stack
				sx={{
					flex: "1 1 auto",
					overflowY: "auto",
				}}
			>
				{children}
			</Stack>
		</Drawer>
	);
};
